import type {
  DashboardRefreshTriggerResultType,
  QuickSightDashboardDetailType,
  QuickSightDashboardType,
  QuickSightSessionsType,
} from "../schemas/InsightsSchemas.ts";
import type { ResourceAdapterToAdapterQueryKeysType } from "../util/adapter.ts";
import { Resources } from "../util/resources.ts";
import { restAPI } from "./api.ts";

type GetDashboardType = {
  name?: string;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
};

export interface InsightsAdapterType {
  getDashboards: (
    params: GetDashboardType
  ) => Promise<QuickSightDashboardType[]>;
  getDashboardsV2: (
    params: GetDashboardType
  ) => Promise<QuickSightDashboardType[]>;
  getDashboardDetails: (params: {
    insightsId: string;
  }) => Promise<QuickSightDashboardDetailType>;
  getDashboardDetailsV2: (params: {
    insightsId: string;
  }) => Promise<QuickSightDashboardDetailType>;
  getInsightsSessions: () => Promise<QuickSightSessionsType>;
  refreshDashboardDatasets: (params: {
    insightsId: string;
  }) => Promise<DashboardRefreshTriggerResultType>;
  refreshDashboardDatasetsV2: (params: {
    insightsId: string;
  }) => Promise<DashboardRefreshTriggerResultType>;
}

export const Insights: InsightsAdapterType = {
  /**
   * Get dashboards visible to the Quicksight user associated with the current user in the default namespace
   * @returns the insights list dashboards
   */
  getDashboards: ({
    name,
    sortBy = "name",
    sortDirection = "asc",
  }: GetDashboardType) =>
    restAPI.insightsAPI.GET<QuickSightDashboardType[]>({
      endpoint: "/dashboards",
      query: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        name,
      },
    }),

  /**
   * Get dashboards visible to the Quicksight user associated with the current user in the company namespace
   * @returns the details for an insights dashboard with an embed url
   */
  getDashboardsV2: ({
    name,
    sortBy = "name",
    sortDirection = "asc",
  }: GetDashboardType) =>
    restAPI.insightsAPI.GET<QuickSightDashboardType[]>({
      endpoint: "/v2/dashboards",
      query: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        name,
      },
    }),

  /**
   * Get details of an insights dashboard in the default namespace
   * @returns the details for an insights dashboard with an embed url
   */
  getDashboardDetails: ({ insightsId }: { insightsId: string }) =>
    restAPI.insightsAPI.GET<QuickSightDashboardDetailType>({
      endpoint: `/dashboards/${insightsId}/embed_url`,
    }),

  /**
   * Get details of an insights dashboard in the company namespace
   * @returns the details for an insights dashboard with an embed url
   */
  getDashboardDetailsV2: ({ insightsId }: { insightsId: string }) =>
    restAPI.insightsAPI.GET<QuickSightDashboardDetailType>({
      endpoint: `/v2/dashboards/${insightsId}/embed_url`,
    }),

  /**
   * Get a temporary session URL and authorization code to embed a QuickSight console session in a website/application
   * @returns insights embed url
   */
  getInsightsSessions: () =>
    restAPI.insightsAPI.GET<QuickSightSessionsType>({
      endpoint: `/sessions/embed_url`,
    }),

  refreshDashboardDatasets: ({ insightsId }: { insightsId: string }) =>
    restAPI.insightsAPI.POST<DashboardRefreshTriggerResultType>({
      endpoint: `/dashboards/${insightsId}/refresh`,
    }),

  refreshDashboardDatasetsV2: ({ insightsId }: { insightsId: string }) =>
    restAPI.insightsAPI.POST<DashboardRefreshTriggerResultType>({
      endpoint: `/v2/dashboards/${insightsId}/refresh`,
    }),
};

export const InsightsAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<InsightsAdapterType> & {
  /**
   * Cache key for a dashboard thumbnail's pre-signed download URL, keyed by the
   * dashboard's optional `thumbnail_ref` file id.
   */
  getDashboardThumbnail: (params: {
    thumbnailRef?: string;
  }) => readonly unknown[];
} = {
  getDashboards: (params) =>
    [Resources.DASHBOARDS_INSIGHTS, "getDashboards", params] as const,
  getDashboardsV2: (params) =>
    [Resources.DASHBOARDS_INSIGHTS, "getDashboardsV2", params] as const,
  getDashboardDetails: (params) =>
    [
      Resources.DASHBOARDS_INSIGHTS,
      "getDashboardDetails",
      params.insightsId,
    ] as const,
  getDashboardDetailsV2: (params) =>
    [
      Resources.DASHBOARDS_INSIGHTS,
      "getDashboardDetailsV2",
      params.insightsId,
    ] as const,
  getInsightsSessions: () =>
    [Resources.DASHBOARDS_INSIGHTS, "getInsightsSessions"] as const,
  getDashboardThumbnail: ({ thumbnailRef }) =>
    [
      Resources.DASHBOARDS_INSIGHTS,
      "dashboard-thumbnail",
      thumbnailRef,
    ] as const,
  refreshDashboardDatasets: (params) =>
    [
      Resources.DASHBOARDS_INSIGHTS,
      "refreshDashboardDatasets",
      params.insightsId,
    ] as const,
  refreshDashboardDatasetsV2: (params) =>
    [
      Resources.DASHBOARDS_INSIGHTS,
      "refreshDashboardDatasetsV2",
      params.insightsId,
    ] as const,
};
